import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productAPI, reviewAPI, productQuestionAPI, IMAGE_BASE } from '../../services/api';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import useScrollReveal from '../../hooks/useScrollReveal';
import useSeo from '../../hooks/useSeo';
import { ArrowLeft, CheckCircle, XCircle, Package, ChevronLeft, ChevronRight, ShoppingBag, ShoppingCart, Check, Minus, Plus, X, Star, MessageSquare, ChevronDown, HelpCircle, Send, Truck, ShieldCheck, RefreshCw } from 'lucide-react';
import { useCart } from '../../context/CartContext';

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [selectedImage, setSelectedImage] = useState(0);
    const [loading, setLoading] = useState(true);
    const revealRef = useScrollReveal();
    const { addItem, loading: cartLoading } = useCart();
    const { customer } = useCustomerAuth();
    const [qty, setQty] = useState(1);
    const [activeTab, setActiveTab] = useState('description');

    // Zoom state
    const [zoomActive, setZoomActive] = useState(false);
    const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [mobileModalIndex, setMobileModalIndex] = useState(0);
    const galleryMainRef = useRef(null);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when mobile modal is open
    useEffect(() => {
        if (mobileModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileModalOpen]);

    // Reviews state
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ total: 0, avgRating: 0, distribution: [] });

    // Q&A state
    const [questions, setQuestions] = useState([]);
    const [questionText, setQuestionText] = useState('');
    const [questionLoading, setQuestionLoading] = useState(false);
    const [questionMsg, setQuestionMsg] = useState('');

    // Variant state
    const [selectedAttributes, setSelectedAttributes] = useState({});
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef({});

    // Standalone size state (non-variant products)
    const [selectedSize, setSelectedSize] = useState(null);
    const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
    const sizeDropdownRef = useRef(null);

    // Close variant/size dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (openDropdown && dropdownRefs.current[openDropdown] && !dropdownRefs.current[openDropdown].contains(e.target)) {
                setOpenDropdown(null);
            }
            if (sizeDropdownOpen && sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target)) {
                setSizeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [openDropdown, sizeDropdownOpen]);

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            setRelatedProducts([]);
            try {
                const res = await productAPI.getBySlug(slug);
                setProduct(res.data);
                // Initialize variant selection with first values
                if (res.data.variantOptions?.length > 0) {
                    const initial = {};
                    res.data.variantOptions.forEach(opt => {
                        initial[opt.name] = opt.values[0] || '';
                    });
                    setSelectedAttributes(initial);
                }
                // Initialize standalone size selection
                if (!res.data.hasVariants && res.data.sizes?.length > 0) {
                    setSelectedSize(res.data.sizes[0]);
                } else {
                    setSelectedSize(null);
                }
                // Fetch related products from same category
                if (res.data.categoryId) {
                    try {
                        const relRes = await productAPI.getAll({ category: res.data.categoryId, limit: 10 });
                        const others = (relRes.data.products || relRes.data || []).filter(p => p.id !== res.data.id);
                        setRelatedProducts(others.slice(0, 8));
                    } catch (e) { /* ignore */ }
                }
                // Fetch reviews & questions
                try {
                    const revRes = await reviewAPI.getProductReviews(res.data.id);
                    setReviews(revRes.data.reviews || []);
                    setReviewStats(revRes.data.stats || { total: 0, avgRating: 0, distribution: [] });
                } catch (e) { /* ignore */ }
                try {
                    const qRes = await productQuestionAPI.getProductQuestions(res.data.id);
                    setQuestions(qRes.data || []);
                } catch (e) { /* ignore */ }
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchProduct();
        window.scrollTo(0, 0);
    }, [slug]);

    const getImageUrl = (imgObj) => {
        if (!imgObj) return '';
        return imgObj.url?.startsWith('http') ? imgObj.url : IMAGE_BASE + imgObj.url;
    };

    const VIDEO_EXTS = ['.mp4', '.webm', '.mov'];
    const isVideo = (item) => item?.mediaType === 'video' || VIDEO_EXTS.some(ext => (item?.url || '').toLowerCase().split('?')[0].endsWith(ext));

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    // Find selected variant
    const selectedVariant = useMemo(() => {
        if (!product?.hasVariants || !product?.variants?.length) return null;
        const attrs = Object.entries(selectedAttributes);
        if (attrs.length === 0) return null;
        return product.variants.find(v => {
            const va = v.attributes || [];
            return attrs.every(([name, value]) => va.some(a => a.name === name && a.value === value));
        }) || null;
    }, [product, selectedAttributes]);

    // Determine which images to show
    const displayImages = useMemo(() => {
        if (selectedVariant?.images?.length > 0) {
            return selectedVariant.images;
        }
        return product?.images || [];
    }, [selectedVariant, product]);

    // Reset selected image when images change
    useEffect(() => {
        setSelectedImage(0);
    }, [displayImages]);

    const handleManualNav = (idx) => {
        setSelectedImage(idx);
    };

    // Determine display price, stock, sku
    const displayPrice = selectedVariant ? parseFloat(selectedVariant.price) : parseFloat(product?.price || 0);
    const displayComparePrice = selectedVariant ? (selectedVariant.comparePrice ? parseFloat(selectedVariant.comparePrice) : null) : (product?.comparePrice ? parseFloat(product.comparePrice) : null);
    const displayStock = selectedVariant ? selectedVariant.stock : (product?.stock || 0);
    const displaySku = selectedVariant?.sku || product?.sku;
    const discount = displayComparePrice ? Math.round((1 - displayPrice / displayComparePrice) * 100) : 0;

    const handleAskQuestion = async () => {
        if (!questionText.trim()) return;
        setQuestionLoading(true);
        try {
            await productQuestionAPI.create({ productId: product.id, question: questionText.trim() });
            setQuestionText('');
            setQuestionMsg('Sorunuz gönderildi! Cevaplanınca burada yayınlanacaktır.');
            setTimeout(() => setQuestionMsg(''), 4000);
        } catch (e) {
            setQuestionMsg(e.response?.data?.error || 'Soru gönderilemedi');
            setTimeout(() => setQuestionMsg(''), 3000);
        }
        setQuestionLoading(false);
    };

    useSeo(product ? {
        title: `${product.name} | ${product.category?.name || 'Tablolar'} | Atölye Kirpi`,
        description: product.shortDescription || `${product.name} - Atölye Kirpi ${product.category?.name || 'tablolar'} koleksiyonu. Özgün tasarım, kaliteli malzeme. Hızlı kargo, güvenli ödeme.`,
        keywords: `${product.name}, ${product.category?.name || ''}, tablolar, Atölye Kirpi, online alışveriş`,
        canonical: `https://xn--atlyekirpi-fcb.com.tr/urun/${product.slug}`
    } : {});

    return (
        <div className="product-detail" ref={revealRef}>
            {loading ? (
                <div className="loading"><div className="spinner"></div></div>
            ) : !product ? (
                <div className="empty-state">
                    <XCircle size={48} strokeWidth={1.5} />
                    <h3>Ürün bulunamadı</h3>
                    <Link to="/urunler" className="btn btn-primary" style={{ marginTop: '1rem' }}>Ürünlere Dön</Link>
                </div>
            ) : (
            <>
            <nav className="pd-breadcrumb">
                <Link to="/">Ana Sayfa</Link>
                <ChevronRight size={14} />
                <Link to="/urunler">Ürünler</Link>
                {product.category?.parent?.parent && (
                    <>
                        <ChevronRight size={14} />
                        <Link to={`/urunler?category=${product.category.parent.parent.id}`}>{product.category.parent.parent.name}</Link>
                    </>
                )}
                {product.category?.parent && (
                    <>
                        <ChevronRight size={14} />
                        <Link to={`/urunler?category=${product.category.parent.id}`}>{product.category.parent.name}</Link>
                    </>
                )}
                {product.category && (
                    <>
                        <ChevronRight size={14} />
                        <Link to={`/urunler?category=${product.category.id}`}>{product.category.name}</Link>
                    </>
                )}
                <ChevronRight size={14} />
                <span>{product.name}</span>
            </nav>

            <div className="product-detail-grid reveal">
                {/* Gallery */}
                <div className="product-gallery">
                    <div
                        className={`product-gallery-main ${zoomActive ? 'zoom-active' : ''}`}
                        ref={galleryMainRef}
                        onMouseEnter={() => { if (!isMobile && !isVideo(displayImages[selectedImage])) setZoomActive(true); }}
                        onMouseLeave={() => setZoomActive(false)}
                        onMouseMove={(e) => {
                            if (!zoomActive || !galleryMainRef.current) return;
                            const rect = galleryMainRef.current.getBoundingClientRect();
                            const x = ((e.clientX - rect.left) / rect.width) * 100;
                            const y = ((e.clientY - rect.top) / rect.height) * 100;
                            setZoomPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
                        }}
                        onClick={() => {
                            if (isMobile && !isVideo(displayImages[selectedImage]) && displayImages.length > 0) {
                                setMobileModalIndex(selectedImage);
                                setMobileModalOpen(true);
                            }
                        }}
                    >
                        {displayImages.length > 0 ? (
                            <>
                                {isVideo(displayImages[selectedImage]) ? (
                                    <video
                                        key={selectedImage}
                                        src={getImageUrl(displayImages[selectedImage])}
                                        controls
                                        autoPlay
                                        muted
                                        playsInline
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <>
                                        <img src={getImageUrl(displayImages[selectedImage])} alt={product.name} draggable={false} />
                                        {zoomActive && (
                                            <div
                                                className="zoom-lens"
                                                style={{
                                                    left: `${zoomPos.x}%`,
                                                    top: `${zoomPos.y}%`,
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                                {displayImages.length > 1 && (
                                    <>
                                        <button className="gallery-nav gallery-nav-left" onClick={(e) => { e.stopPropagation(); handleManualNav(selectedImage === 0 ? displayImages.length - 1 : selectedImage - 1); }}>
                                            <ChevronLeft size={20} />
                                        </button>
                                        <button className="gallery-nav gallery-nav-right" onClick={(e) => { e.stopPropagation(); handleManualNav((selectedImage + 1) % displayImages.length); }}>
                                            <ChevronRight size={20} />
                                        </button>
                                    </>
                                )}
                            </>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                                <Package size={64} strokeWidth={1} />
                            </div>
                        )}
                    </div>

                    {/* Desktop Zoom Panel */}
                    {zoomActive && displayImages[selectedImage] && !isVideo(displayImages[selectedImage]) && (
                        <div
                            className="zoom-result"
                            style={{
                                backgroundImage: `url(${getImageUrl(displayImages[selectedImage])})`,
                                backgroundPosition: `${zoomPos.x}% ${zoomPos.y}%`,
                            }}
                        />
                    )}
                    {displayImages.length > 1 && (
                        <div className="product-gallery-thumbs">
                            {displayImages.map((img, idx) => (
                                <div key={img.id || idx} className={`product-gallery-thumb ${selectedImage === idx ? 'active' : ''}`} onClick={() => handleManualNav(idx)}>
                                    {isVideo(img) ? (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--accent-primary)' }}><polygon points="5,3 19,12 5,21" /></svg>
                                        </div>
                                    ) : (
                                        <img src={getImageUrl(img)} alt={img.alt || product.name} />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="product-info">
                    {product.category && (
                        <Link to={`/urunler?category=${product.category.id}`} className="category-link">{product.category.name}</Link>
                    )}
                    <h1>{product.name}</h1>
                    {displaySku && <p className="pd-sku">SKU: {displaySku}</p>}

                    <div className="product-price-section">
                        <span className="current">{formatPrice(displayPrice)}</span>
                        {displayComparePrice && <span className="compare">{formatPrice(displayComparePrice)}</span>}
                        {discount > 0 && <span className="discount">%{discount} İndirim</span>}
                    </div>

                    {/* Variant Selector */}
                    {product.hasVariants && product.variantOptions?.length > 0 && (
                        <div className="variant-selector">
                            {product.variantOptions.map(opt => {
                                const isOpen = openDropdown === opt.name;
                                const hasColors = opt.colorCodes && Object.keys(opt.colorCodes).length > 0;
                                const selectedColor = hasColors ? opt.colorCodes[selectedAttributes[opt.name]] : null;
                                return (
                                <div key={opt.name} className="variant-group" ref={el => dropdownRefs.current[opt.name] = el}>
                                    <button
                                        type="button"
                                        className={`variant-dropdown-trigger ${isOpen ? 'open' : ''}`}
                                        onClick={() => setOpenDropdown(isOpen ? null : opt.name)}
                                    >
                                        <span className="variant-dropdown-label">
                                            {opt.name}
                                        </span>
                                        <span className="variant-dropdown-value">
                                            {hasColors && selectedColor && (
                                                <span className="variant-dropdown-color-dot" style={{ background: selectedColor }} />
                                            )}
                                            {selectedAttributes[opt.name] || 'Seçiniz'}
                                        </span>
                                        <ChevronDown size={16} className={`variant-dropdown-chevron ${isOpen ? 'open' : ''}`} />
                                    </button>
                                    {isOpen && (
                                        <div className="variant-dropdown-menu">
                                            {opt.values.map(val => {
                                                const isSelected = selectedAttributes[opt.name] === val;
                                                const testAttrs = { ...selectedAttributes, [opt.name]: val };
                                                const matchingVariant = product.variants.find(v => {
                                                    const va = v.attributes || [];
                                                    return Object.entries(testAttrs).every(([n, vl]) => va.some(a => a.name === n && a.value === vl));
                                                });
                                                const isAvailable = matchingVariant?.isActive !== false;
                                                const colorCode = hasColors ? opt.colorCodes[val] : null;
                                                return (
                                                    <button
                                                        key={val}
                                                        type="button"
                                                        className={`variant-dropdown-item ${isSelected ? 'selected' : ''} ${!isAvailable ? 'unavailable' : ''}`}
                                                        onClick={() => {
                                                            if (!isAvailable) return;
                                                            setSelectedAttributes(prev => ({ ...prev, [opt.name]: val }));
                                                            setOpenDropdown(null);
                                                        }}
                                                    >
                                                        {colorCode && (
                                                            <span className="variant-dropdown-item-dot" style={{ background: colorCode }} />
                                                        )}
                                                        <span>{val}</span>
                                                        {isSelected && <Check size={15} />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Standalone Size Selector (non-variant products) */}
                    {!product.hasVariants && product.sizes?.length > 0 && (
                        <div className="variant-selector">
                            <div className="variant-group" ref={sizeDropdownRef}>
                                <button
                                    type="button"
                                    className={`variant-dropdown-trigger ${sizeDropdownOpen ? 'open' : ''}`}
                                    onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                                >
                                    <span className="variant-dropdown-label">Beden</span>
                                    <span className="variant-dropdown-value">
                                        {selectedSize || 'Seçiniz'}
                                    </span>
                                    <ChevronDown size={16} className={`variant-dropdown-chevron ${sizeDropdownOpen ? 'open' : ''}`} />
                                </button>
                                {sizeDropdownOpen && (
                                    <div className="variant-dropdown-menu">
                                        {product.sizes.map(size => (
                                            <button
                                                key={size}
                                                type="button"
                                                className={`variant-dropdown-item ${selectedSize === size ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setSelectedSize(size);
                                                    setSizeDropdownOpen(false);
                                                }}
                                            >
                                                <span>{size}</span>
                                                {selectedSize === size && <Check size={15} />}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="product-stock-info">
                        {displayStock > 0 ? (
                            <CheckCircle size={16} color="var(--success)" />
                        ) : (
                            <XCircle size={16} color="var(--danger)" />
                        )}
                        <span className="pd-stock-text">
                            {displayStock === 0 ? 'Stokta Yok' : displayStock <= (product.minStock || 5) ? 'Sınırlı Stok' : 'Stokta Var'}
                        </span>
                    </div>

                    {product.shortDescription && (
                        <p className="pd-short-desc">{product.shortDescription}</p>
                    )}

                    {/* Sepete Ekle */}
                    {displayStock > 0 && (
                        <div className="product-order-box">
                            <div className="pd-divider" />
                            <div className="product-order-row">
                                <div className="product-qty-control">
                                    <button onClick={() => setQty(q => Math.max(1, q - 1))} disabled={qty <= 1}><Minus size={16} /></button>
                                    <span>{qty}</span>
                                    <button onClick={() => setQty(q => Math.min(displayStock, q + 1))} disabled={qty >= displayStock}><Plus size={16} /></button>
                                </div>
                                <button
                                    className="product-add-cart-btn"
                                    disabled={cartLoading}
                                    onClick={() => addItem(product.id, selectedVariant?.id || null, qty, selectedSize)}
                                >
                                    <ShoppingBag size={18} />
                                    <span>{cartLoading ? 'Ekleniyor...' : 'Sepete Ekle'}</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Trust Badges */}
                    <div className="pd-trust-badges">
                        <div className="pd-trust-item">
                            <Truck size={16} />
                            <span>Ücretsiz Kargo</span>
                        </div>
                        <div className="pd-trust-item">
                            <ShieldCheck size={16} />
                            <span>Güvenli Ödeme</span>
                        </div>
                        <div className="pd-trust-item">
                            <RefreshCw size={16} />
                            <span>Kolay İade</span>
                        </div>
                    </div>

                </div>
            </div>

            {/* Tabs */}
            <div className="pd-tabs">
                <div className="pd-tabs-nav">
                    <button className={`pd-tab-btn ${activeTab === 'description' ? 'active' : ''}`} onClick={() => setActiveTab('description')}>
                        <Package size={17} /> Açıklama
                    </button>
                    <button className={`pd-tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
                        <Star size={17} /> Yorumlar {reviewStats.total > 0 && <span className="pd-tab-badge">{reviewStats.total}</span>}
                    </button>
                    <button className={`pd-tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>
                        <HelpCircle size={17} /> Soru & Cevap {questions.length > 0 && <span className="pd-tab-badge">{questions.length}</span>}
                    </button>
                </div>

                <div className="pd-tab-content">
                    {activeTab === 'description' && (
                        product.description ? (
                            <div className="pd-desc-content" dangerouslySetInnerHTML={{ __html: product.description }} />
                        ) : (
                            <div className="pd-reviews-empty">
                                <Package size={32} strokeWidth={1} />
                                <p>Ürün açıklaması henüz eklenmemiş</p>
                            </div>
                        )
                    )}

                    {activeTab === 'reviews' && (
                        reviewStats.total > 0 ? (
                            <>
                                <div className="pd-reviews-summary">
                                    <div className="pd-reviews-avg">
                                        <span className="pd-reviews-avg-num">{reviewStats.avgRating}</span>
                                        <div className="pd-reviews-avg-stars">
                                            {[1,2,3,4,5].map(s => (
                                                <Star key={s} size={18} fill={s <= Math.round(reviewStats.avgRating) ? 'var(--warning)' : 'none'} color={s <= Math.round(reviewStats.avgRating) ? 'var(--warning)' : 'var(--text-muted)'} />
                                            ))}
                                        </div>
                                        <span className="pd-reviews-avg-total">{reviewStats.total} değerlendirme</span>
                                    </div>
                                    <div className="pd-reviews-bars">
                                        {(reviewStats.distribution || []).map(d => (
                                            <div key={d.star} className="pd-reviews-bar-row">
                                                <span>{d.star}</span>
                                                <Star size={12} fill="var(--warning)" color="var(--warning)" />
                                                <div className="pd-reviews-bar-track">
                                                    <div className="pd-reviews-bar-fill" style={{ width: `${reviewStats.total ? (d.count / reviewStats.total) * 100 : 0}%` }} />
                                                </div>
                                                <span className="pd-reviews-bar-count">{d.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="pd-reviews-list">
                                    {reviews.map(review => (
                                        <div key={review.id} className="pd-review-card">
                                            <div className="pd-review-header">
                                                <div className="pd-review-avatar">
                                                    {review.customer?.firstName?.[0]}{review.customer?.lastName?.[0]}
                                                </div>
                                                <div>
                                                    <strong>{review.customer?.firstName} {review.customer?.lastName?.[0]}.</strong>
                                                    <div className="pd-review-stars">
                                                        {[1,2,3,4,5].map(s => (
                                                            <Star key={s} size={14} fill={s <= review.rating ? 'var(--warning)' : 'none'} color={s <= review.rating ? 'var(--warning)' : 'var(--text-muted)'} />
                                                        ))}
                                                    </div>
                                                </div>
                                                <span className="pd-review-date">{new Date(review.createdAt).toLocaleDateString('tr-TR')}</span>
                                            </div>
                                            <p className="pd-review-comment">{review.comment}</p>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="pd-reviews-empty">
                                <MessageSquare size={32} strokeWidth={1} />
                                <p>Henüz yorum yapılmamış</p>
                            </div>
                        )
                    )}

                    {activeTab === 'qa' && (
                        <div className="pd-qa-section">
                            <div className="pd-qa-form">
                                {customer ? (
                                    <>
                                        <h4>Bu ürün hakkında bir sorunuz mu var?</h4>
                                        {questionMsg && <p className="pd-qa-msg">{questionMsg}</p>}
                                        <div className="pd-qa-input-row">
                                            <input
                                                type="text"
                                                placeholder="Sorunuzu yazın..."
                                                value={questionText}
                                                onChange={e => setQuestionText(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && questionText.trim()) handleAskQuestion(); }}
                                                maxLength={500}
                                            />
                                            <button
                                                className="btn btn-primary pd-qa-send-btn"
                                                disabled={questionLoading || !questionText.trim()}
                                                onClick={handleAskQuestion}
                                            >
                                                <Send size={16} /> Gönder
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="pd-qa-login-prompt">
                                        <HelpCircle size={20} />
                                        <span>Soru sormak için <Link to="/giris">giriş yapın</Link></span>
                                    </div>
                                )}
                            </div>

                            {questions.length > 0 ? (
                                <div className="pd-qa-list">
                                    {questions.map(q => (
                                        <div key={q.id} className="pd-qa-card">
                                            <div className="pd-qa-question">
                                                <div className="pd-qa-icon pd-qa-icon-q">S</div>
                                                <div className="pd-qa-body">
                                                    <p>{q.question}</p>
                                                    <span className="pd-qa-meta">
                                                        {q.customer?.firstName} {q.customer?.lastName?.[0]}. - {new Date(q.createdAt).toLocaleDateString('tr-TR')}
                                                    </span>
                                                </div>
                                            </div>
                                            {q.answer && (
                                                <div className="pd-qa-answer">
                                                    <div className="pd-qa-icon pd-qa-icon-a">C</div>
                                                    <div className="pd-qa-body">
                                                        <p>{q.answer}</p>
                                                        <span className="pd-qa-meta">Satıcı</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="pd-reviews-empty">
                                    <HelpCircle size={32} strokeWidth={1} />
                                    <p>Henüz soru sorulmamış</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Accessories */}
            {product.accessories?.length > 0 && (
                <div className="acc-section">
                    <h2>Birlikte Alınan Ürünler</h2>
                    <div className="acc-grid">
                        {product.accessories.map(acc => {
                            const accImg = acc.images?.[0]?.url;
                            const accImgUrl = accImg ? (accImg.startsWith('http') ? accImg : IMAGE_BASE + accImg) : null;
                            const accPrice = parseFloat(acc.price || 0);
                            const accCompare = acc.comparePrice ? parseFloat(acc.comparePrice) : null;
                            const accDiscount = accCompare ? Math.round((1 - accPrice / accCompare) * 100) : 0;
                            return (
                                <div key={acc.id} className="acc-card">
                                    <Link to={`/urun/${acc.slug}`} className="acc-card-link">
                                        <div className="acc-card-img">
                                            {accImgUrl ? (
                                                <img src={accImgUrl} alt={acc.name} loading="lazy" />
                                            ) : (
                                                <Package size={28} strokeWidth={1} />
                                            )}
                                            {accDiscount > 0 && <span className="acc-discount">%{accDiscount}</span>}
                                        </div>
                                        <div className="acc-card-info">
                                            <h4>{acc.name}</h4>
                                            <div className="acc-card-price">
                                                <span className="current">{formatPrice(accPrice)}</span>
                                                {accCompare && <span className="compare">{formatPrice(accCompare)}</span>}
                                            </div>
                                        </div>
                                    </Link>
                                    <button
                                        className="acc-add-btn"
                                        title="Sepete Ekle"
                                        onClick={() => addItem(acc.id, null, 1)}
                                        disabled={cartLoading || acc.stock <= 0}
                                    >
                                        <Plus size={18} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Related Products */}
            {relatedProducts.length > 0 && (
                <div className="related-products">
                    <h2>Benzer Ürünler</h2>
                    <div className="related-products-grid">
                        {relatedProducts.map(rp => {
                            const rpImg = rp.images?.[0]?.url;
                            const rpImgUrl = rpImg ? (rpImg.startsWith('http') ? rpImg : IMAGE_BASE + rpImg) : null;
                            const rpPrice = parseFloat(rp.price || 0);
                            const rpCompare = rp.comparePrice ? parseFloat(rp.comparePrice) : null;
                            const rpDiscount = rpCompare ? Math.round((1 - rpPrice / rpCompare) * 100) : 0;
                            return (
                                <Link to={`/urun/${rp.slug}`} key={rp.id} className="related-product-card">
                                    <div className="related-product-img">
                                        {rpImgUrl ? (
                                            <img src={rpImgUrl} alt={rp.name} loading="lazy" />
                                        ) : (
                                            <Package size={32} strokeWidth={1} />
                                        )}
                                        {rpDiscount > 0 && <span className="related-product-discount">%{rpDiscount}</span>}
                                    </div>
                                    <div className="related-product-info">
                                        <h4>{rp.name}</h4>
                                        <div className="related-product-price">
                                            <span className="current">{formatPrice(rpPrice)}</span>
                                            {rpCompare && <span className="compare">{formatPrice(rpCompare)}</span>}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* Mobile Fullscreen Modal */}
            {mobileModalOpen && (
                <div className="mobile-zoom-overlay" onClick={() => setMobileModalOpen(false)}>
                    <button className="mobile-zoom-close" onClick={() => setMobileModalOpen(false)}>
                        <X size={24} />
                    </button>
                    <div className="mobile-zoom-counter">
                        {mobileModalIndex + 1} / {displayImages.filter(img => !isVideo(img)).length}
                    </div>
                    <div className="mobile-zoom-img-wrap" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={getImageUrl(displayImages[mobileModalIndex])}
                            alt={product.name}
                            draggable={false}
                        />
                    </div>
                    {displayImages.length > 1 && (
                        <>
                            <button className="mobile-zoom-nav mobile-zoom-nav-left" onClick={(e) => { e.stopPropagation(); setMobileModalIndex(mobileModalIndex === 0 ? displayImages.length - 1 : mobileModalIndex - 1); }}>
                                <ChevronLeft size={28} />
                            </button>
                            <button className="mobile-zoom-nav mobile-zoom-nav-right" onClick={(e) => { e.stopPropagation(); setMobileModalIndex((mobileModalIndex + 1) % displayImages.length); }}>
                                <ChevronRight size={28} />
                            </button>
                        </>
                    )}
                </div>
            )}
            </>
            )}
        </div>
    );
}
